<template>
  <div :style="fullWidth ? 'width:95%' : `width:${dimensions * 2.5}px;`">
    <div
      class="outerDiv mx-auto"
      :style="outerDivStyle"
      @click="clicked"
      ref="outerDiv"
    >
      <div class="crossContainer" :style="containerStyle">
        <v-img
          v-if="imagePath"
          :src="imagePath"
          contain
          class="backgroundImageClass"
        />
        <heat-map
          v-if="showHeatmap && obj"
          :size="dimensions"
          :kObj="type == 'keratometry' ? obj : null"
          :rxObj="type == 'NaFl' || type == 'rx' ? obj : null"
          style="opacity: 1"
          :type="type"
        ></heat-map>

        <div class="circle" v-else-if="showLens && !showEyepiece"></div>
        <div v-if="showAxisLine" class="axisLineDiv" :style="lensRotationStyle">
          <div
            class="axisLine"
            :style="`height:${dimensions * 0.15}px; margin-top:${
              (dimensions * 0.9) / 2 - dimensions * 0.15
            }px;`"
          ></div>
        </div>
        <div
          class="ovalHighlight"
          v-if="showLens && !showEyepiece && !showHeatmap"
        ></div>
        <!-- <topography
          v-show="showHeatmap"
          :dimensions="dimensions"
          :obj="obj"
          :type="type"
        /> -->

        <div class="crosses" :style="rotationStyle">
          <div class="line m1Line"></div>
          <div class="line m2Line"></div>
        </div>
        <div class="labels" :style="rotationStyle">
          <div
            v-if="vertPower != ''"
            class="label topLabel"
            :style="topLabelStyle"
            v-html="vertPower"
          ></div>
          <div
            v-if="rightLabelText != ''"
            class="label rightLabel"
            :class="{
              'd-none': !showMeridianLabels && rightLabelType == 'meridian',
            }"
            :style="rightLabelStyle"
            v-html="rightLabelText"
          ></div>
          <div
            v-if="bottomLabelText != ''"
            class="label bottomLabel"
            :class="{ 'd-none': !showMeridianLabels }"
            :style="bottomLabelStyle"
            v-html="bottomLabelText"
          ></div>
          <div
            v-if="leftLabelText != ''"
            class="label leftLabel"
            :class="{
              'd-none': !showMeridianLabels && leftLabelType == 'meridian',
            }"
            :style="leftLabelStyle"
            v-html="leftLabelText"
          ></div>
        </div>
        <div
          v-if="meridianMarker"
          class="crosses"
          :style="meridianRotationStyle"
        >
          <div class="line meridianLine"></div>
          <div class="line meridianLine2"></div>
        </div>
      </div>
    </div>

    <div v-show="showCaption" class="captionStyle">
      <!-- :style="{ width: dimensions + 'px' }" -->
      {{ rxString }}
    </div>
    <slot name="moreStuff" class="mt-1"></slot>

    <div
      v-if="canRotate"
      class="d-flex flex-row text-center align-start justify-center my-2"
    >
      <v-btn
        rounded
        outlined
        x-small
        depressed
        color="primary"
        class="mx-1"
        @click="rotationChange(-10)"
        :tabindex="-1"
        ><v-icon small>mdi-chevron-double-left</v-icon></v-btn
      >
      <v-btn
        rounded
        outlined
        x-small
        depressed
        color="primary"
        class="mx-1"
        @click="rotationChange(-1)"
        :tabindex="-1"
        ><v-icon small>mdi-chevron-left</v-icon></v-btn
      >
      <div class="mx-1 caption text-center" style="min-width: 35px">
        {{ Math.abs(lensRotation) }}&deg;
        <span v-if="lensRotation > 0"> R</span>
        <span v-if="lensRotation < 0"> L</span>
      </div>
      <v-btn
        rounded
        outlined
        x-small
        depressed
        color="primary"
        class="mx-1"
        @click="rotationChange(1)"
        :tabindex="-1"
        ><v-icon small>mdi-chevron-right</v-icon></v-btn
      >
      <v-btn
        rounded
        outlined
        x-small
        depressed
        color="primary"
        class="mx-1"
        @click="rotationChange(+10)"
        :tabindex="-1"
        ><v-icon small>mdi-chevron-double-right</v-icon></v-btn
      >
    </div>
    <!-- {{ meridianRotationStyle }} -->
  </div>
</template>

<script>
import RxMethods from "@/assets/js/cls/RxMethods";
// import Topography from "@/components/clStuff/Topography";
// import RxObject from "../assets/js/RxObject";

export default {
  name: "OpticalCrossImage",
  mixins: [RxMethods],
  components: {
    HeatMap: () => import("@/components/calcsStuff/HeatMap"),
  },
  props: {
    obj: {},
    // keratometry: {},
    type: { type: String, default: "refraction" }, // or "keratometry" or "NaFl"
    dimensions: { type: Number, default: 100 },
    fullWidth: null, // if true the outermost container will be set to 95% width
    showCaption: { type: Boolean, default: false },
    showLens: { type: Boolean, default: true },
    showEyepiece: null,
    image: null,
    showHeatmap: null,
    showMeridianLabels: { type: Boolean, default: true },
    labelFontSize: { type: String, default: "10" },
    meridianFontSize: { type: String, default: "10" },
    showAxisLine: null,
    canRotate: null,
    lensRotation: { type: Number, default: 0 },
    customLabels: null, // provide custom labels in this format {vertPower:"",horizPower:"",vertMeridian:"",horizMeridian:""}
    showAsHybrid: null,
    meridianMarker: null, // shows a line indicating the meridian. meridianMarker should be a number (the angle of the meridian)
  },
  data() {
    return {
      labelCss:
        "color: #212121; background-color: #ffffff; border: 1px solid #9e9e9e; border-radius: 10%; box-shadow: 2px 2px 5px #b9b9b994;",
      meridianCss:
        "color: #212121; background-color:rgb(255 255 224); border-radius: 10%; border: 1px solid #9e9e9e;",
      eyepiecePath: "https://www.eyedock.com/images/crosses/eyering_2.svg",

      maxLensRotation: 90,
    };
  },
  methods: {
    formatDiopters: function (num) {
      if (!num) return "0.00";
      let needSign = !(this.type == "keratometry");
      return this.numToDiopterString(num, needSign);
    },
    rotationChange(delta) {
      let newRotation = this.lensRotation + delta;
      this.emitRotation(newRotation);
    },
    emitRotation(newRotation) {
      if (newRotation < -this.maxLensRotation)
        newRotation = -this.maxLensRotation;
      if (newRotation > this.maxLensRotation)
        newRotation = this.maxLensRotation;
      this.$emit("rotation", newRotation);
    },
    clicked(e) {
      const left = this.$refs.outerDiv.getBoundingClientRect().left;
      const top = this.$refs.outerDiv.getBoundingClientRect().top;
      const w = this.$refs.outerDiv.getBoundingClientRect().width;
      const h = this.$refs.outerDiv.getBoundingClientRect().height;
      const yHalf = h / 2; // horiz midline
      const xHalf = w / 2; // vert midline
      const x = e.clientX - left;
      const y = e.clientY - top;
      const dY = y - yHalf;
      const dX = x - xHalf;
      if (y < yHalf) return; // ignore if the top half of the image

      let angle = Math.floor((Math.atan(dX / dY) * 180) / Math.PI);
      this.emitRotation(angle);
      // console.log("width", w, "height", h);
      // console.log("x", x, "y", y);
      // console.log("dX", dX, "dY", dY);
      // console.log("currentAngle", angle);
    },
  },
  computed: {
    outerDivStyle: function () {
      let width = Math.floor(this.dimensions * 2.5) + "px; ";
      let height = Math.floor(this.dimensions * 1.8);
      if (!this.showMeridianLabels) height -= 25; // don't need as much height if the meridian labels are hidden
      height += "px; ";
      // let topMargin = -Math.floor(this.dimensions / 3);
      // let rightMargin = Math.floor(this.dimensions / 2) + 10;
      // let margin = `${topMargin}px ${rightMargin}px 0 15px`;
      let margin = 0;
      return `width:${width}px; height:${height}px; margin: ${margin}; padding-top:10px;`;
    },
    containerStyle: function () {
      let sz = this.dimensions;
      return `width:${sz}px; height:${sz}px; `;
    },
    rotationStyle: function () {
      let rotation = Number(this.rotation) - Number(this.lensRotation);
      return "transform: rotate(" + rotation + "deg); ";
    },
    lensRotationStyle: function () {
      // todo calculate translateY
      return (
        "transform: rotate(" +
        -this.lensRotation +
        "deg) translateX(50%) translateY(50%); "
      );
      // translateY(70px);
    },
    meridianRotationStyle: function () {
      // console.log(this.meridianMarker);
      let angle = 90 - this.meridianMarker;
      return (
        "transform: rotate(" +
        angle +
        "deg);  translateX(50%) translateY(50%); "
      );
    },
    labelStyle: function () {
      let fontSize = Math.floor(this.dimensions * 0.09);
      if (this.labelFontSize) fontSize = this.labelFontSize;

      let labelWidth = Math.max(40, Math.floor(fontSize * 4.3));
      // let lineHeight = Math.floor(fontSize * 2);
      let rotation = Number(-this.rotation) + Number(this.lensRotation);
      let style = "padding: 2px; transform: rotate(" + rotation + "deg) ; ";
      style += "line-height: " + Math.max(9, fontSize) + "pt; ";
      style += "font-size: " + fontSize + "pt; ";
      if (!this.customLabels) style += " white-space: nowrap; ";
      // style += "width:" + labelWidth + "px; ";
      style +=
        "margin: -" + labelWidth / 4 + "px auto 0 -" + labelWidth / 2 + "px; ";
      return style;
    },
    topLabelStyle: function () {
      return this.labelStyle + this.labelCss;
    },
    bottomLabelStyle: function () {
      let rtn = this.labelStyle + this.meridianCss;
      if (this.meridianFontSize)
        rtn += "font-size: " + this.meridianFontSize + "pt !important; ";
      return rtn;
    },
    leftLabelType: function () {
      let meridian = this.vertMeridian;
      let isPower =
        (meridian >= 45 && meridian < 90) ||
        (meridian >= 135 && meridian < 180);
      return isPower ? "power" : "meridian";
    },
    rightLabelType: function () {
      let meridian = this.vertMeridian;
      let isPower =
        (meridian < 45 && meridian > 0) || (meridian < 135 && meridian >= 90);
      return isPower ? "power" : "meridian";
    },
    // todo - consolidate some of these properties? .. there's a lot of duplication..
    leftLabelStyle: function () {
      let rtn = this.labelStyle;
      if (this.leftLabelType == "power") {
        rtn += this.labelCss;
      } else {
        rtn += this.meridianCss;
        if (this.meridianFontSize)
          rtn += "font-size: " + this.meridianFontSize + "pt !important; ";
      }
      return rtn;
    },
    rightLabelStyle: function () {
      let rtn = this.labelStyle;
      if (this.rightLabelType == "power") {
        rtn += this.labelCss;
      } else {
        rtn += this.meridianCss;
      }
      return rtn;
    },
    leftLabelText: function () {
      return this.leftLabelType == "power"
        ? this.horizPower
        : this.horizMeridianText;
    },
    rightLabelText: function () {
      return this.rightLabelType == "power"
        ? this.horizPower
        : this.horizMeridianText;
    },
    bottomLabelText: function () {
      if (this.customLabels?.vertMeridian)
        return this.customLabels.vertMeridian;
      return this.vertMeridian + "&deg;";
    },

    rotation: function () {
      let vertMeridian = this.vertMeridian;
      // console.log(vertMeridian, 90 - vertMeridian);
      return 90 - vertMeridian;
    },
    rxString: function () {
      // console.log(this.rx);
      if (!this.obj) return "";
      let str =
        this.type == "keratometry"
          ? this.prettyFullKstring(this.obj)
          : this.prettyString(this.obj);
      return str;
    },
    vertPower: function () {
      // console.log(this.rx);
      if (this.customLabels?.vertPower) return this.customLabels.vertPower;
      if (!this.obj)
        return this.type == "keratometry"
          ? "&nbsp;&nbsp;&nbsp;?&nbsp;&nbsp;&nbsp;"
          : "&nbsp;0.00&nbsp;";
      let vertPower =
        this.type == "keratometry"
          ? this.verticalKpower(this.obj)
          : this.verticalPower(this.obj);
      return this.formatDiopters(vertPower);
    },
    horizPower: function () {
      // console.log(this.rx);
      if (this.customLabels?.horizPower) return this.customLabels.horizPower;
      if (!this.obj)
        return this.type == "keratometry"
          ? "&nbsp;&nbsp;&nbsp;?&nbsp;&nbsp;&nbsp;"
          : "&nbsp;0.00&nbsp;";
      let horizPower =
        this.type == "keratometry"
          ? this.horizontalKpower(this.obj)
          : this.horizontalPower(this.obj);
      return this.formatDiopters(horizPower);
    },
    vertMeridianText: function () {
      if (this.customLabels?.vertMeridian)
        return this.customLabels.vertMeridian;
      return this.vertMeridian + "&deg;";
    },
    vertMeridian: function () {
      if (!this.obj) return "090";
      // console.log(this.rx);
      let meridian =
        this.type == "keratometry"
          ? this.vertKmeridian(this.obj)
          : this.verticalMeridian(this.obj);
      return this.numToAxisString(meridian);
    },
    horizMeridianText: function () {
      if (this.customLabels?.horizMeridian)
        return this.customLabels.horizMeridian;
      return this.horizMeridian + "&deg;";
    },
    horizMeridian: function () {
      // console.log(this.rx);
      if (!this.obj) return "180";
      let meridian =
        this.type == "keratometry"
          ? this.horizKmeridian(this.obj)
          : this.horizontalMeridian(this.obj);
      return this.numToAxisString(meridian);
    },
    // customLabelsForMeridian: function () {
    //   if (!this.overrideLabels) return null;
    //   let rtn = { vert: null, horiz: null };
    //   let meridians = Object.keys(this.overrideLabels);
    //   meridians.forEach((meridian) => {
    //     let label = this.overrideLabels[meridian];
    //     let num = parseInt(meridian);
    //     if (num >= 45 && num < 135) {
    //       rtn.vert = label;
    //     } else if (num < 45 || num >= 135) {
    //       rtn.horiz = label;
    //     }
    //   });
    //   return rtn;
    // },
    imagePath: function () {
      let p = null;
      if (this.image) p = this.image.path;
      if (this.showEyepiece) p = this.eyepiecePath;
      return p;
    },
  },
};
</script>

<style scoped>
.outerDiv {
  /* border: 1px solid lightblue; */
  position: relative;
}
.crossContainer {
  /* border: 1px solid lightblue; */
  position: relative;
  margin: 2em auto;
}

.circle {
  /* border: 1px solid #b4b4b4; */
  border-radius: 50%;
  width: 90%;
  height: 90%;
  margin: 5%;
  background-image: linear-gradient(#d7faff, #d7faff, #accfd4);
  box-shadow: 0px 1px 4px 0px #5a5a5a8f;
  /* box-shadow: 0 0 0 3px #9090900f; */
}

.ovalHighlight {
  position: absolute;
  background-color: #fff;
  opacity: 0.5;
  filter: blur(3px);
  top: 14%;
  left: 24%;
  width: 55%;
  height: 26%;
  /* moz-border-radius: 50px / 20px;
  webkit-border-radius: 50px / 20px; */
  border-radius: 80%;
}
.crosses,
.labels {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  transition: transform 0.25s;
}
.line {
  position: absolute;
  top: 0px;
  left: 0px;
}

.m1Line {
  width: 50%;
  height: 100%;
  border-right: 1px dashed #797979;
}
.m2Line {
  width: 100%;
  height: 50%;
  border-bottom: 1px dashed #797979;
}

.meridianLine {
  width: 50%;
  height: 100%;
  border-right: 2px solid #ffffff;
}
.meridianLine2 {
  width: 50%;
  height: 100%;
  border-right: 2px dashed #16ff05;
}

.label {
  position: absolute;
  text-align: center;
  padding: 0 2px;

  /* width: 40px; */
  /* margin: -10px auto 0 -20px;
  font-size: 8pt; */
  /* font-family: Roboto, Trebuchet, sans-serif; */
}

.topLabel {
  top: -10%;
  left: 50%;
  background-color: white;
  border: 1px solid lightgray;
  border-radius: 3px;
}
.rightLabel {
  left: 110%;
  top: 50%;
}
.bottomLabel {
  top: 115%;
  left: 50%;
}
.leftLabel {
  left: -15%;
  top: 50%;
}

.captionStyle {
  margin-top: 30px;
  text-align: center;
}
.axisLineDiv {
  position: absolute;
  top: 0;
  left: calc(50% - 1px);
  width: 0;
  height: 100%;
  /* margin: 5%; */
  background-color: yellow;
  transition: transform 0.25s;
}
.axisLine {
  /* height: 1em; */
  border-right: 2px solid rgb(18, 23, 80);
  background-color: rgb(139, 243, 150);
  /* margin-top: 70px; */
}
.backgroundImageClass {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 50%;
  /* margin: 5%; */
  transform: translateX(-50%);
  opacity: 0.7;
  border-radius: 50%;
  box-shadow: #0000002e 0 2px 5px 2px;
}
</style>
